"use client";
import { cn } from "../../lib/utilsComprehensive";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMotionPreference } from "../../hooks/useMotionPreference";
import { Motion, OptimizedGlass } from "../../primitives";
import { useA11yId } from "../../utils/a11y";
import { useGlassSound } from "../../utils/soundDesign";
import {
  ContrastGuard,
  TextWithContrast,
} from "@/components/accessibility/ContrastGuard";
import { ANIMATION } from "../../tokens/designConstants";
import { useReducedMotion } from "../../hooks/useReducedMotion";

export interface GanttTask {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  progress: number; // 0-100
  dependencies?: string[];
  assignee?: {
    id: string;
    name: string;
    avatar?: string;
  };
  status?:
    | "not-started"
    | "in-progress"
    | "completed"
    | "blocked"
    | "cancelled";
  priority?: "low" | "medium" | "high" | "critical";
  color?: string;
  description?: string;
  milestone?: boolean;
  parent?: string;
  children?: string[];
  customData?: Record<string, unknown>;
}

export interface GanttTimeScale {
  unit: "day" | "week" | "month" | "quarter" | "year";
  step: number;
}

export interface GanttViewOptions {
  showWeekends?: boolean;
  showToday?: boolean;
  showProgress?: boolean;
  showDependencies?: boolean;
  showMilestones?: boolean;
  showCriticalPath?: boolean;
  showBaseline?: boolean;
  showResources?: boolean;
}

export interface GlassGanttChartProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Tasks data */
  tasks?: GanttTask[];
  /** Chart start date */
  startDate?: Date;
  /** Chart end date */
  endDate?: Date;
  /** Time scale configuration */
  timeScale?: GanttTimeScale;
  /** View options */
  viewOptions?: GanttViewOptions;
  /** Chart height */
  height?: number;
  /** Row height */
  rowHeight?: number;
  /** Column width for time units */
  columnWidth?: number;
  /** Whether tasks can be edited */
  editable?: boolean;
  /** Whether to show task hierarchy */
  showHierarchy?: boolean;
  /** Task click handler */
  onTaskClick?: (task: GanttTask) => void;
  /** Task update handler */
  onTaskUpdate?: (task: GanttTask) => void;
  /** Task resize handler */
  onTaskResize?: (taskId: string, startDate: Date, endDate: Date) => void;
  /** Task move handler */
  onTaskMove?: (taskId: string, startDate: Date, endDate: Date) => void;
  /** Progress update handler */
  onProgressUpdate?: (taskId: string, progress: number) => void;
  /** Dependency create handler */
  onDependencyCreate?: (fromTask: string, toTask: string) => void;
  /** Dependency delete handler */
  onDependencyDelete?: (fromTask: string, toTask: string) => void;
  /** Custom task renderer */
  renderTask?: (
    task: GanttTask,
    bounds: { x: number; width: number; y: number; height: number }
  ) => React.ReactNode;
  /** Custom timeline header renderer */
  renderTimelineHeader?: (date: Date, unit: string) => React.ReactNode;
  /** Respect motion preferences */
  respectMotionPreference?: boolean;
}

export const GlassGanttChart = forwardRef<HTMLDivElement, GlassGanttChartProps>(
  (
    {
      tasks = [],
      startDate,
      endDate,
      timeScale = { unit: "day", step: 1 },
      viewOptions = {
        showWeekends: true,
        showToday: true,
        showProgress: true,
        showDependencies: true,
        showMilestones: true,
        showCriticalPath: false,
        showBaseline: false,
        showResources: true,
      },
      height = 320,
      rowHeight = 36,
      columnWidth = 40,
      editable = false,
      showHierarchy = true,
      onTaskClick,
      onTaskUpdate,
      onTaskResize,
      onTaskMove,
      onProgressUpdate,
      onDependencyCreate,
      onDependencyDelete,
      renderTask,
      renderTimelineHeader,
      respectMotionPreference = true,
      className,
      ...props
    },
    ref
  ) => {
    const { shouldAnimate } = useMotionPreference();
    const { play } = useGlassSound();
    const ganttId = useA11yId("glass-gantt-chart");

    const [selectedTask, setSelectedTask] = useState<string | null>(null);
    const [dragState, setDragState] = useState<{
      isDragging: boolean;
      taskId?: string;
      startX?: number;
      startDate?: Date;
      mode?: "move" | "resize-start" | "resize-end";
    }>({ isDragging: false });
    const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
    const [hoveredTask, setHoveredTask] = useState<string | null>(null);

    const chartRef = useRef<HTMLDivElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);
    const taskListRef = useRef<HTMLDivElement>(null);

    // Calculate date range
    const dateRange = useMemo(() => {
      const taskDates = tasks.flatMap((task) => [task.startDate, task.endDate]);
      const now = new Date();
      const minDate =
        startDate ||
        (taskDates.length
          ? new Date(Math.min(...taskDates.map((d: any) => d.getTime())))
          : now);
      const maxDate =
        endDate ||
        (taskDates.length
          ? new Date(Math.max(...taskDates.map((d: any) => d.getTime())))
          : new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000));

      const paddedStart = new Date(minDate);
      if (!startDate) {
        paddedStart.setDate(paddedStart.getDate() - 7);
      }

      const paddedEnd = new Date(maxDate);
      if (!endDate) {
        paddedEnd.setDate(paddedEnd.getDate() + 7);
      }

      return { start: paddedStart, end: paddedEnd };
    }, [tasks, startDate, endDate]);

    // Generate time columns
    const timeColumns = useMemo(() => {
      const columns: Date[] = [];
      const current = new Date(dateRange.start);

      while (current <= dateRange.end) {
        columns.push(new Date(current));

        switch (timeScale.unit) {
          case "day":
            current.setDate(current.getDate() + timeScale.step);
            break;
          case "week":
            current.setDate(current.getDate() + 7 * timeScale.step);
            break;
          case "month":
            current.setMonth(current.getMonth() + timeScale.step);
            break;
          case "quarter":
            current.setMonth(current.getMonth() + 3 * timeScale.step);
            break;
          case "year":
            current.setFullYear(current.getFullYear() + timeScale.step);
            break;
        }
      }

      return columns;
    }, [dateRange, timeScale]);

    // Calculate task positions
    const getTaskPosition = useCallback(
      (task: GanttTask) => {
        const totalDuration =
          dateRange.end.getTime() - dateRange.start.getTime();
        const taskStart = task.startDate.getTime() - dateRange.start.getTime();
        const taskDuration = task.endDate.getTime() - task.startDate.getTime();

        const x =
          (taskStart / totalDuration) * (timeColumns.length * columnWidth);
        const width =
          (taskDuration / totalDuration) * (timeColumns.length * columnWidth);

        return { x, width: Math.max(width, 20) }; // Minimum width of 20px
      },
      [dateRange, timeColumns.length, columnWidth]
    );

    // Status colors
    const statusColors = {
      "not-started": "#94a3b8",
      "in-progress": "#60a5fa",
      completed: "#34d399",
      blocked: "#f87171",
      cancelled: "#64748b",
    };

    // Priority colors
    const priorityColors = {
      low: "glass-border-success",
      medium: "glass-border-warning",
      high: "glass-border-warning",
      critical: "glass-border-danger",
    };

    // Handle task drag start
    const handleTaskDragStart = useCallback(
      (
        e: React.MouseEvent,
        taskId: string,
        mode: "move" | "resize-start" | "resize-end"
      ) => {
        if (!editable) return;

        const task = tasks.find((t) => t.id === taskId);
        if (!task) return;

        setDragState({
          isDragging: true,
          taskId,
          startX: e.clientX,
          startDate: task.startDate,
          mode,
        });

        play("drag_start");
      },
      [editable, tasks, play]
    );

    // Handle task drag
    const handleTaskDrag = useCallback(
      (e: React.MouseEvent) => {
        if (
          !dragState.isDragging ||
          !dragState.taskId ||
          !dragState.startX ||
          !dragState.startDate
        )
          return;

        const deltaX = e.clientX - dragState.startX;
        const totalDuration =
          dateRange.end.getTime() - dateRange.start.getTime();
        const timePerPixel = totalDuration / (timeColumns.length * columnWidth);
        const deltaTime = deltaX * timePerPixel;

        const task = tasks.find((t) => t.id === dragState.taskId);
        if (!task) return;

        if (dragState.mode === "move") {
          const newStartDate = new Date(
            dragState.startDate.getTime() + deltaTime
          );
          const taskDuration =
            task.endDate.getTime() - task.startDate.getTime();
          const newEndDate = new Date(newStartDate.getTime() + taskDuration);

          onTaskMove?.(dragState.taskId, newStartDate, newEndDate);
        } else if (dragState.mode === "resize-end") {
          const newEndDate = new Date(task.endDate.getTime() + deltaTime);
          if (newEndDate > task.startDate) {
            onTaskResize?.(dragState.taskId, task.startDate, newEndDate);
          }
        } else if (dragState.mode === "resize-start") {
          const newStartDate = new Date(task.startDate.getTime() + deltaTime);
          if (newStartDate < task.endDate) {
            onTaskResize?.(dragState.taskId, newStartDate, task.endDate);
          }
        }
      },
      [
        dragState,
        dateRange,
        timeColumns.length,
        columnWidth,
        tasks,
        onTaskMove,
        onTaskResize,
      ]
    );

    // Handle task drag end
    const handleTaskDragEnd = useCallback(() => {
      if (dragState.isDragging) {
        setDragState({ isDragging: false });
        play("drag_end");
      }
    }, [dragState.isDragging, play]);

    // Handle progress update
    const handleProgressUpdate = useCallback(
      (taskId: string, e: React.MouseEvent, progressBarWidth: number) => {
        if (!editable) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const progress = Math.max(
          0,
          Math.min(100, (x / progressBarWidth) * 100)
        );

        onProgressUpdate?.(taskId, Math.round(progress));
        play("progress_update");
      },
      [editable, onProgressUpdate, play]
    );

    // Format date for header
    const formatHeaderDate = useCallback(
      (date: Date) => {
        switch (timeScale.unit) {
          case "day":
            return date.getDate().toString();
          case "week":
            return `W${Math.ceil(date.getDate() / 7)}`;
          case "month":
            return date.toLocaleDateString("default", { month: "short" });
          case "quarter":
            return `Q${Math.ceil((date.getMonth() + 1) / 3)}`;
          case "year":
            return date.getFullYear().toString();
          default:
            return date.toLocaleDateString();
        }
      },
      [timeScale.unit]
    );

    // Organize tasks by hierarchy
    const organizedTasks = useMemo(() => {
      if (!showHierarchy) return tasks;

      const taskMap = new Map(tasks.map((task: any) => [task.id, task]));
      const roots: GanttTask[] = [];
      const children = new Map<string, GanttTask[]>();

      // Build hierarchy
      tasks.forEach((task: any) => {
        if (task.parent) {
          const parentChildren = children.get(task.parent) || [];
          parentChildren.push(task);
          children.set(task.parent, parentChildren);
        } else {
          roots.push(task);
        }
      });

      // Flatten with hierarchy
      const flattened: GanttTask[] = [];
      const addTasksRecursively = (taskList: GanttTask[], level = 0) => {
        taskList.forEach((task: any) => {
          flattened.push({
            ...task,
            customData: { ...task.customData, level },
          });
          const taskChildren = children.get(task.id);
          if (taskChildren) {
            addTasksRecursively(taskChildren, level + 1);
          }
        });
      };

      addTasksRecursively(roots);
      return flattened;
    }, [tasks, showHierarchy]);

    // Default task renderer
    const defaultRenderTask = useCallback(
      (
        task: GanttTask,
        bounds: { x: number; width: number; y: number; height: number }
      ) => {
        const isSelected = selectedTask === task.id;
        const isHovered = hoveredTask === task.id;
        const level = (task.customData?.level as number) || 0;

        if (task.milestone) {
          return (
            <div
              data-glass-component
              className={cn(
                "glass-absolute glass-flex glass-items-center glass-justify-center glass-cursor-pointer",
                "glass-border-2 glass-border-primary"
              )}
              style={{
                left: bounds.x,
                top: bounds.y + bounds.height / 4,
                width: bounds.height / 2,
                height: bounds.height / 2,
                transform: `rotate(45deg) scale(${isHovered ? 1.1 : 1})`,
                background: task.color
                  ? `color-mix(in srgb, ${task.color} 22%, transparent)`
                  : "rgba(112, 214, 255, 0.22)",
                borderColor: task.color || "#70d6ff",
                boxShadow: isSelected
                  ? "0 0 0 2px rgba(112, 214, 255, 0.45)"
                  : undefined,
              }}
              onClick={() => onTaskClick?.(task)}
              onMouseEnter={() => setHoveredTask(task.id)}
              onMouseLeave={() => setHoveredTask(null)}
              title={`${task.name} (Milestone)`}
            />
          );
        }

        return (
          <OptimizedGlass
            elevation="level2"
            intensity="medium"
            depth={1}
            tint="neutral"
            border="subtle"
            className={cn(
              `glass-absolute glass-cursor-pointer glass-transition-all glass-duration-[${ANIMATION.DURATION.fast}ms]`,
              "glass-backdrop-blur-sm glass-border glass-border-subtle glass-radius-md",
              "glass-overflow-hidden",
              task.priority && priorityColors[task.priority],
              isHovered && "glass-shadow-lg"
            )}
            style={{
              left: bounds.x,
              top: bounds.y + 4,
              width: bounds.width,
              height: bounds.height - 8,
              overflow: "hidden",
              marginLeft: showHierarchy ? level * 20 : 0,
              // Keep bars inside their row. Scaling here caused the bottom bar
              // to cross the chart's clipped viewport during pointer/QA passes.
              transform: undefined,
              boxShadow: isSelected
                ? "0 0 0 2px rgba(112, 214, 255, 0.45)"
                : undefined,
            }}
            onClick={() => {
              setSelectedTask(task.id);
              onTaskClick?.(task);
            }}
            onMouseEnter={() => setHoveredTask(task.id)}
            onMouseLeave={() => setHoveredTask(null)}
            onMouseDown={(e: React.MouseEvent) =>
              handleTaskDragStart(e, task.id, "move")
            }
          >
            {/* Task Bar */}
            <div
              className="glass-relative glass-flex glass-items-center"
              style={{ height: "calc(100% - 2px)" }}
            >
              {/* Status Color */}
              <div
                className="glass-absolute glass-left-0 glass-top-0 glass-h-full glass-w-1 glass-rounded-l"
                style={{
                  backgroundColor: task.status
                    ? statusColors[task.status]
                    : statusColors["not-started"],
                }}
              />

              {/* Progress Bar */}
              {viewOptions.showProgress && task.progress > 0 && (
                <div
                  className={`glass-absolute glass-left-1 glass-top-1 glass-bottom-1 glass-surface-primary/30 glass-radius-sm glass-transition-all glass-duration-[${ANIMATION.DURATION.normal}ms]`}
                  style={{
                    width: `${(task.progress / 100) * (bounds.width - 8)}px`,
                  }}
                  onClick={(e: React.MouseEvent) =>
                    handleProgressUpdate(task.id, e, bounds.width - 8)
                  }
                />
              )}

              {/* Task Content */}
              <div className="glass-flex-1 glass-px-3 glass-min-glass-w-0">
                <ContrastGuard>
                  <div className="glass-text-sm glass-font-medium glass-text-primary glass-truncate">
                    {task.name}
                  </div>
                </ContrastGuard>
                {viewOptions.showResources && task.assignee && (
                  <ContrastGuard>
                    <div className="glass-text-xs glass-text-secondary glass-truncate">
                      {task.assignee.name}
                    </div>
                  </ContrastGuard>
                )}
              </div>

              {/* Progress Percentage */}
              {viewOptions.showProgress && (
                <ContrastGuard>
                  <div className="glass-text-xs glass-text-secondary glass-px-2">
                    {task.progress}%
                  </div>
                </ContrastGuard>
              )}

              {/* Resize Handles */}
              {editable && isSelected && (
                <>
                  <div
                    className="glass-absolute glass-left-0 glass-top-0 glass-bottom-0 glass-w-1 glass-cursor-ew-resize glass-surface-primary/20 hover:glass-surface-primary/40"
                    onMouseDown={(e: React.MouseEvent) =>
                      handleTaskDragStart(e, task.id, "resize-start")
                    }
                  />
                  <div
                    className="glass-absolute glass-right-0 glass-top-0 glass-bottom-0 glass-w-1 glass-cursor-ew-resize glass-surface-primary/20 hover:glass-surface-primary/40"
                    onMouseDown={(e: React.MouseEvent) =>
                      handleTaskDragStart(e, task.id, "resize-end")
                    }
                  />
                </>
              )}
            </div>
          </OptimizedGlass>
        );
      },
      [
        selectedTask,
        hoveredTask,
        priorityColors,
        statusColors,
        showHierarchy,
        viewOptions.showProgress,
        viewOptions.showResources,
        editable,
        onTaskClick,
        handleTaskDragStart,
        handleProgressUpdate,
      ]
    );

    // Handle scroll synchronization
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      const scrollLeft = e.currentTarget.scrollLeft;
      const scrollTop = e.currentTarget.scrollTop;

      setScrollPosition({ x: scrollLeft, y: scrollTop });

      if (timelineRef.current) {
        timelineRef.current.scrollLeft = scrollLeft;
      }
      if (taskListRef.current) {
        taskListRef.current.scrollTop = scrollTop;
      }
    }, []);

    return (
      <OptimizedGlass
        ref={ref}
        id={ganttId}
        elevation="level1"
        intensity="subtle"
        depth={1}
        tint="neutral"
        border="subtle"
        className={cn(
          "glass-gantt-chart glass-radius-lg glass-backdrop-blur-md glass-border glass-border-subtle glass-overflow-hidden glass-max-w-full",
          className
        )}
        style={{
          height: `${height}px`,
          maxHeight: "calc(100vh - 48px)",
          minHeight: Math.min(height, 260),
          minWidth: 0,
        }}
        onMouseMove={handleTaskDrag}
        onMouseUp={handleTaskDragEnd}
        onMouseLeave={handleTaskDragEnd}
        {...props}
      >
        <Motion
          preset={shouldAnimate && respectMotionPreference ? "fadeIn" : "none"}
          className="glass-h-full glass-flex glass-flex-col"
        >
          {/* Header */}
          <div className="glass-flex glass-border-b glass-border-subtle">
            {/* Task List Header */}
            <div className="glass-w-32 glass-p-3 glass-border-r glass-border-subtle glass-surface-dark/20">
              <ContrastGuard>
                <h3 className="glass-text-sm glass-font-semibold glass-text-primary">
                  Tasks
                </h3>
              </ContrastGuard>
            </div>

            {/* Timeline Header */}
            <div
              ref={timelineRef}
              className="glass-flex-1 glass-surface-dark/20"
              style={{
                minWidth: 0,
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <div
                className="glass-flex glass-h-16"
                style={{ width: timeColumns.length * columnWidth }}
              >
                {timeColumns.map((date, index) => {
                  const isToday =
                    viewOptions.showToday &&
                    date.toDateString() === new Date().toDateString();
                  const isWeekend =
                    !viewOptions.showWeekends &&
                    (date.getDay() === 0 || date.getDay() === 6);

                  return (
                    <div
                      key={index}
                      className={cn(
                        "glass-flex-shrink-0 glass-border-r glass-border-subtle glass-flex glass-items-center glass-justify-center glass-text-xs",
                        isToday &&
                          "glass-surface-primary/10 glass-text-primary glass-font-semibold",
                        isWeekend &&
                          "glass-surface-muted/20 glass-text-secondary"
                      )}
                      style={{ width: columnWidth }}
                    >
                      <ContrastGuard>
                        {renderTimelineHeader
                          ? renderTimelineHeader(date, timeScale.unit)
                          : formatHeaderDate(date)}
                      </ContrastGuard>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="glass-flex glass-flex-1 glass-overflow-hidden">
            {/* Task List */}
            <div
              ref={taskListRef}
              className="glass-w-32 glass-border-r glass-border-subtle glass-overflow-y-hidden glass-surface-dark/20"
            >
              <div>
                {organizedTasks.map((task, index) => {
                  const level = (task.customData?.level as number) || 0;

                  return (
                    <Motion
                      key={task.id}
                      preset={
                        shouldAnimate && respectMotionPreference
                          ? "slideUp"
                          : "none"
                      }
                      delay={index * 50}
                    >
                      <div
                        className={cn(
                          "glass-flex glass-items-center glass-p-2 glass-border-b glass-border-subtle glass-cursor-pointer glass-transition-colors",
                          "hover:glass-surface-overlay",
                          selectedTask === task.id &&
                            "glass-surface-primary/10 glass-text-primary"
                        )}
                        style={{
                          height: rowHeight,
                          paddingLeft: showHierarchy ? 8 + level * 16 : 8,
                        }}
                        onClick={() => {
                          setSelectedTask(task.id);
                          onTaskClick?.(task);
                        }}
                      >
                        {showHierarchy &&
                          task.children &&
                          task.children.length > 0 && (
                            <button className="glass-mr-2 glass-text-secondary hover:glass-text-primary">
                              ▼
                            </button>
                          )}

                        <div className="glass-flex-1 glass-min-glass-w-0">
                          <ContrastGuard>
                            <div className="glass-text-sm glass-font-medium glass-text-primary glass-truncate">
                              {task.name}
                            </div>
                          </ContrastGuard>
                          {task.assignee && (
                            <ContrastGuard>
                              <div className="glass-text-xs glass-text-secondary glass-truncate">
                                {task.assignee.name}
                              </div>
                            </ContrastGuard>
                          )}
                        </div>

                        {task.milestone && (
                          <div className="glass-w-3 glass-h-3 glass-surface-primary glass-radius-full glass-transform glass-rotate-45 glass-ml-2" />
                        )}
                      </div>
                    </Motion>
                  );
                })}
              </div>
            </div>

            {/* Chart Area */}
            <div
              ref={chartRef}
              className="glass-flex-1 glass-overflow-auto glass-relative"
              onScroll={handleScroll}
            >
              <div
                className="glass-relative"
                style={{
                  width: timeColumns.length * columnWidth,
                  height: organizedTasks.length * rowHeight,
                }}
              >
                {/* Grid Lines */}
                <div className="glass-absolute glass-inset-0">
                  {/* Vertical lines */}
                  {timeColumns.map((date, index) => {
                    const isToday =
                      viewOptions.showToday &&
                      date.toDateString() === new Date().toDateString();
                    const isWeekend =
                      date.getDay() === 0 || date.getDay() === 6;

                    return (
                      <div
                        key={index}
                        className={cn(
                          "glass-absolute glass-top-0 glass-bottom-0 glass-border-r",
                          isToday
                            ? "glass-border-primary glass-surface-primary/5"
                            : "glass-border-subtle",
                          !viewOptions.showWeekends &&
                            isWeekend &&
                            "glass-surface-muted/10"
                        )}
                        style={{ left: index * columnWidth }}
                      />
                    );
                  })}

                  {/* Horizontal lines */}
                  {organizedTasks.map((_, index) => (
                    <div
                      key={index}
                      className="glass-absolute glass-left-0 glass-right-0 glass-border-b glass-border-subtle"
                      style={{ top: (index + 1) * rowHeight }}
                    />
                  ))}
                </div>

                {/* Tasks */}
                {organizedTasks.map((task, index) => {
                  const position = getTaskPosition(task);
                  const bounds = {
                    x: position.x,
                    width: position.width,
                    y: index * rowHeight,
                    height: rowHeight,
                  };

                  return (
                    <Motion
                      key={task.id}
                      preset={
                        shouldAnimate && respectMotionPreference
                          ? "slideRight"
                          : "none"
                      }
                      delay={index * 100}
                    >
                      {renderTask
                        ? renderTask(task, bounds)
                        : defaultRenderTask(task, bounds)}
                    </Motion>
                  );
                })}

                {/* Dependencies */}
                {viewOptions.showDependencies &&
                  organizedTasks.map((task: any) =>
                    task.dependencies?.map((depId: any) => {
                      const depTask = organizedTasks.find(
                        (t) => t.id === depId
                      );
                      if (!depTask) return null;

                      const taskIndex = organizedTasks.indexOf(task);
                      const depIndex = organizedTasks.indexOf(depTask);
                      const taskPos = getTaskPosition(task);
                      const depPos = getTaskPosition(depTask);

                      return (
                        <svg
                          key={`${task.id}-${depId}`}
                          className="glass-absolute glass-pointer-events-none"
                          style={{
                            left: 0,
                            top: 0,
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <line
                            x1={depPos.x + depPos.width}
                            y1={depIndex * rowHeight + rowHeight / 2}
                            x2={taskPos.x}
                            y2={taskIndex * rowHeight + rowHeight / 2}
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeDasharray="4,4"
                            className="glass-text-primary-glass-opacity-40"
                          />
                          <polygon
                            points={`${taskPos.x - 6},${taskIndex * rowHeight + rowHeight / 2 - 3} ${taskPos.x},${taskIndex * rowHeight + rowHeight / 2} ${taskPos.x - 6},${taskIndex * rowHeight + rowHeight / 2 + 3}`}
                            fill="currentColor"
                            className="glass-text-primary-glass-opacity-40"
                          />
                        </svg>
                      );
                    })
                  )}
              </div>
            </div>
          </div>
        </Motion>
      </OptimizedGlass>
    );
  }
);

GlassGanttChart.displayName = "GlassGanttChart";

export default GlassGanttChart;
